import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseService } from 'src/app/core/services/base.httpService';
import { handleErrorMessage } from 'src/app/shared/operators/error.operator';
import { AccordionTabs } from 'src/app/shared/utility/constants';
import { URLConstants } from 'src/app/shared/utility/url-constants';
@Injectable({
  providedIn: 'root',
})
export class AccountsPayableService {
  private readonly errorHandler = handleErrorMessage();

  constructor(private readonly baseService: BaseService) {}

  public requiredAccountsPayableData = new BehaviorSubject<any>({});
  public isFromPageSummary = new BehaviorSubject<any>({});
  public isAccountsPayablePrevSummary = new BehaviorSubject<any>('');
  private readonly vendorReconQueueSubject = new BehaviorSubject<any[]>([]);
  public vendorReconQueue$ = this.vendorReconQueueSubject.asObservable();

  private readonly currentVendorReconIndexSubject = new BehaviorSubject<number>(
    0
  );
  public currentVendorReconIndex$ =
    this.currentVendorReconIndexSubject.asObservable();

  public getAccountsPayableBasicSearchData(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.accountsPayableBasicSearch,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public deleteAccountsPayable(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteAccountsPayableType,
        event,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public createAccountsPayable(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'PUT',
        URLConstants.createAccountsPayable,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public updateAccountsPayable(data: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.updateAccountsPayable,
        data,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public fetchAccountsPayable(id: number): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.fetchAccountsPayable + id,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public addAccountsPayableGrid(data: any) {
    return this.baseService
      .initiateApiRequest(
        'PUT',
        URLConstants.addAccountsPayableGrid,
        data,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public editAccountsPayableGrid(data: any) {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.editAccountsPayableGrid,
        data,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public deleteAccountsPayableGrid(data: any) {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteAccountsPayableGrid,
        data,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public validateIdentifierValue(data: any) {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.validateIdentifierValue +
          AccordionTabs.ap +
          '/' +
          data.shipmentIdentifer +
          '/' +
          data.shipmentIdentifierValue,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public setRequiredDataAPChargeCodeInfoPage(data: any): void {
    this.requiredAccountsPayableData.next(data);
  }

  public setPageFrom(data: string) {
    this.isFromPageSummary.next(data);
  }

  public isAPSummaryPrevSearch(data: string) {
    this.isAccountsPayablePrevSummary.next(data);
  }

  public uploadInvoice(file: any): Observable<any> {
    const url = URLConstants.uplaodAPInvoice;
    return this.baseService
      .uploadAPFile(url, file)
      .pipe(this.errorHandler());
  }

  public publishApToGpApi(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.publishApToGp,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public getAllReadyForReconFileNames(): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.getAllReadyForReconFileNames,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public processVendorRecon(data: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'PUT',
        URLConstants.processVendorRecon,
        data,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public setVendorReconQueue(queue: any[]): void {
    this.vendorReconQueueSubject.next(queue);
  }

  public getVendorReconQueue(): any[] {
    return this.vendorReconQueueSubject.getValue();
  }

  public setCurrentVendorReconIndex(index: number): void {
    this.currentVendorReconIndexSubject.next(index);
  }

  public getCurrentVendorReconIndex(): number {
    return this.currentVendorReconIndexSubject.getValue();
  }

  public removeFromVendorReconQueue(index: number): void {
    const currentQueue = this.getVendorReconQueue();
    const updatedQueue = currentQueue.filter((_, i) => i !== index);
    this.setVendorReconQueue(updatedQueue);
    const currentIndex = this.getCurrentVendorReconIndex();
    if (currentIndex >= updatedQueue.length && updatedQueue.length > 0) {
      this.setCurrentVendorReconIndex(updatedQueue.length - 1);
    } else if (updatedQueue.length === 0) {
      this.setCurrentVendorReconIndex(0);
    }
  }

  public clearVendorReconQueue(): void {
    this.vendorReconQueueSubject.next([]);
    this.currentVendorReconIndexSubject.next(0);
  }

  public setCreatedInvoiceIdsForEdit(data: {
    invoiceIds: number[];
    currentInvoiceIndex: number;
    isFromVendorRecon: boolean;
  }): void {
    this.requiredAccountsPayableData.next({
      invoiceIds: data.invoiceIds,
      currentInvoiceIndex: data.currentInvoiceIndex,
      isFromVendorRecon: data.isFromVendorRecon,
    });
  }

  public validateAPInvoiceNumber(invoiceNumber: string): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        `${URLConstants.validateAPInvoiceNumber}/${encodeURIComponent(invoiceNumber)}`,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }
}
